import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Logo from '../../assets/images/Logo.png';
import { useCart } from '../../context/CartContext';

const ChevronDown = () => (
  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
  </svg>
);

const CartIcon = () => (
  <svg className="w-6 h-6 sm:w-8 sm:h-8 text-brand-blue" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);

const UserIcon = () => (
  <svg className="w-6 h-6 sm:w-8 sm:h-8 text-brand-blue" fill="currentColor" viewBox="0 0 24 24">
    <path fillRule="evenodd" d="M18 10a6 6 0 11-12 0 6 6 0 0112 0zm-6 8c2.67 0 8 1.34 8 4v2H4v-2c0-2.66 5.33-4 8-4z" clipRule="evenodd" />
  </svg>
);

const MenuIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
  </svg>
);

const CloseIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

export default function Navbar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { cartCount } = useCart();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Shop', path: '/shop', hasDropdown: true },
    { name: 'Contact', path: '/contact' },
  ];

  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    if (!isHomePage) {
      setIsScrolled(true);
      return undefined;
    }

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isHomePage]);

  const navbarBackground = isHomePage
    ? isScrolled
      ? '#ffffff'
      : '#ffffff7d'
    : '#ffffff';

  return (
    <nav
      className="relative z-30 py-2 shadow-sm transition-colors duration-300 sm:py-3"
      style={{ backgroundColor: navbarBackground }}
    >
      <div className="site-container flex items-center gap-6">

        {/* Left: Logo */}
        <div className="flex-shrink-0">
          <Link to="/">
            <img src={Logo} alt="Optex" className="h-[40px] sm:h-[60px] w-auto object-contain" />
          </Link>
        </div>

        {/* Desktop: Right-Aligned Navigation */}
        <div className="ml-auto hidden lg:flex items-center gap-10 xl:gap-12">
          {navLinks.map((link) => (
            <Link
              key={link.name}
              to={link.path}
              className={`nav-link flex items-center ${location.pathname === link.path ? 'nav-link-active' : ''}`}
            >
              {link.name}
              {link.hasDropdown && <ChevronDown />}
            </Link>
          ))}
        </div>

        {/* Right: Icons & CTA */}
        <div className="ml-auto flex items-center gap-2 sm:gap-6 lg:ml-0">
          <div className="flex items-center gap-1 sm:gap-4">
            <Link to="/cart" aria-label="Cart" className="relative p-1 sm:p-2 hover:opacity-80 transition-opacity">
              <CartIcon />
              {cartCount > 0 && (
                <span className="absolute top-0 right-0 inline-flex items-center justify-center w-4 h-4 text-[10px] font-bold text-white bg-red-500 rounded-full sm:w-5 sm:h-5 sm:text-[11px] translate-x-1 -translate-y-1">
                  {cartCount}
                </span>
              )}
            </Link>
            <Link to="/profile" aria-label="Profile" className="p-1 sm:p-2 hover:opacity-80 transition-opacity">
              <UserIcon />
            </Link>
          </div>

          <Link to="/login" className="btn-appointment hidden sm:block">
            Book Appointment
          </Link>

          {/* Mobile Menu Toggle */}
          <button
            className="lg:hidden p-2 text-brand-blue"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
          </button>
        </div>

      </div>

      {/* Mobile Navigation Dropdown */}
      {isMobileMenuOpen && (
        <div className="lg:hidden absolute top-full left-0 w-full bg-white shadow-xl border-t border-gray-100 py-4 px-6 flex flex-col gap-4 z-50">
          {navLinks.map((link) => (
            <Link
              key={link.name}
              to={link.path}
              className={`nav-link flex items-center justify-between font-bold text-[16px] py-2 border-b border-gray-50 ${location.pathname === link.path ? 'nav-link-active' : ''}`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              {link.name}
              {link.hasDropdown && <ChevronDown />}
            </Link>
          ))}
          <Link
            to="/login"
            className="btn-appointment text-center mt-2 w-full"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Book Appointment
          </Link>
        </div>
      )}
    </nav>
  );
}
